"use client";

import * as React from "react";
import { XIcon } from "lucide-react";

import { cn } from "./utils";

interface DialogContextType {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DialogContext = React.createContext<DialogContextType | null>(null);

function Dialog({
  open,
  onOpenChange,
  children,
  ...props
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpen = open !== undefined ? open : internalOpen;
  const handleOpenChange = onOpenChange || setInternalOpen;

  return (
    <DialogContext.Provider value={{ open: isOpen, onOpenChange: handleOpenChange }}>
      {children}
    </DialogContext.Provider>
  );
}

function DialogTrigger({
  children,
  ...props
}: {
  children: React.ReactNode;
}) {
  const context = React.useContext(DialogContext);
  if (!context) throw new Error("DialogTrigger must be used within Dialog");

  return (
    <div
      onClick={() => context.onOpenChange(true)}
      {...props}
    >
      {children}
    </div>
  );
}

function DialogPortal({ children }: { children: React.ReactNode }) {
  const context = React.useContext(DialogContext);
  if (!context) throw new Error("DialogPortal must be used within Dialog");

  if (!context.open) return null;

  return (
    <div data-slot="dialog-portal">
      {children}
    </div>
  );
}

function DialogClose({ children, ...props }: { children?: React.ReactNode }) {
  const context = React.useContext(DialogContext);
  if (!context) throw new Error("DialogClose must be used within Dialog");

  return (
    <div
      onClick={() => context.onOpenChange(false)}
      {...props}
    >
      {children}
    </div>
  );
}

const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  const context = React.useContext(DialogContext);
  const [isVisible, setIsVisible] = React.useState(false);
  
  if (!context) return null;

  React.useEffect(() => {
    if (context.open) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [context.open]);

  return (
    <div
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-[99998] transition-all duration-300 ease-out",
        context.open ? "bg-black/60 opacity-100" : "bg-black/0 opacity-0",
        className,
      )}
      onClick={() => context.onOpenChange(false)}
      {...props}
    />
  );
});
DialogOverlay.displayName = "DialogOverlay";

function DialogContent({
  className,
  children,
  style,
  ...props
}: React.ComponentProps<"div">) {
  const context = React.useContext(DialogContext);
  const [isAnimating, setIsAnimating] = React.useState(false);
  
  if (!context) return null;

  React.useEffect(() => {
    if (context.open) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 400);
      return () => clearTimeout(timer);
    }
  }, [context.open]);

  return (
    <DialogPortal>
      <DialogOverlay />
      <div
        data-slot="dialog-content"
        className={cn(
          "bg-white/10 backdrop-blur-xl border border-white/20 fixed z-[99999] grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-xl p-6 shadow-2xl sm:max-w-lg text-white",
          isAnimating && "marathon-dialog-animate-in",
          className,
        )}
        style={style}
        {...props}
      >
        {children}
        <button 
          className="absolute top-4 right-4 p-2 sm:p-3 landscape-compact-p-1 landscape:p-2 glass-interactive rounded-lg glass-text flex items-center justify-center" 
          style={{ transition: 'none' }}
          onClick={() => context.onOpenChange(false)}
        >
          <XIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 landscape:h-4 landscape:w-4 glass-icon" />
          <span className="sr-only">Close</span>
        </button>
      </div>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-0 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<"h2">) {
  return (
    <h2
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};